/**
 * Desktop nav rail state: whether the permanent drawer is collapsed to the
 * 72px icon rail. Precedence: session toggle > contextual default (viewports
 * below lg force the rail; an open builder canvas forces it only when the
 * collapse is what lets the picked-items tray dock) > persisted user
 * preference. Toggling inside a forced context is session-scoped so those
 * contexts re-assert their default on the next visit; toggling in a neutral
 * context persists as the standing preference. Update when changing rail
 * defaults or collapse behavior.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

import { builderCanvasDockMinViewport } from '@/components/adaptive-picker/useWorkbenchSplit';
import { DRAWER_WIDTH, NAV_RAIL_WIDTH } from '@/config/navigation';

const STORAGE_KEY = 'deepthink.navRail';

const readStoredPreference = (): boolean | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'rail') return true;
    if (stored === 'expanded') return false;
  } catch {
    // Storage unavailable (private mode / disabled) — fall back to defaults.
  }
  return null;
};

const writeStoredPreference = (isRail: boolean): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, isRail ? 'rail' : 'expanded');
  } catch {
    // Preference simply won't survive a reload.
  }
};

export interface NavRailState {
  /** Whether the permanent drawer is currently the icon rail. */
  isRail: boolean;
  /** Width the layout should reserve for the nav at the sm+ breakpoints. */
  navWidth: number;
  toggleNavRail: () => void;
}

export const useNavRail = (builderCanvasActive: boolean): NavRailState => {
  const theme = useTheme();
  // Below lg the full drawer and a builder canvas can't share the viewport.
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down('lg'));
  // The permanent drawer only exists at sm+; below that the mobile drawer
  // and bottom nav take over, so rail toggles would be invisible.
  const hasPermanentDrawer = useMediaQuery(theme.breakpoints.up('sm'));
  // An open builder canvas forces the rail only when the collapse is the
  // difference-maker for the picked-items tray: wide enough that the rail
  // docks it, not so wide that the expanded drawer docks it anyway. (Media
  // queries read up to a classic scrollbar wider than the layout width the
  // tray actually measures — an accepted ~15px tolerance at the boundaries.)
  const railWouldDockTray = useMediaQuery(
    `(min-width:${builderCanvasDockMinViewport(NAV_RAIL_WIDTH)}px)`
  );
  const expandedWouldDockTray = useMediaQuery(
    `(min-width:${builderCanvasDockMinViewport(DRAWER_WIDTH)}px)`
  );
  const forcedBuilderRail =
    builderCanvasActive && railWouldDockTray && !expandedWouldDockTray;

  const [storedPreference, setStoredPreference] =
    useState(readStoredPreference);
  const [sessionOverride, setSessionOverride] = useState<boolean | null>(null);

  // Entering or leaving the builder canvas re-applies the contextual default;
  // a toggle made inside one context shouldn't leak into the other. Keyed on
  // the canvas itself, not forcedBuilderRail, so a mid-canvas window resize
  // doesn't wipe a manual toggle.
  useEffect(() => {
    setSessionOverride(null);
  }, [builderCanvasActive]);

  const contextDefault =
    isNarrowViewport || forcedBuilderRail ? true : (storedPreference ?? false);
  const isRail = sessionOverride ?? contextDefault;

  const toggleNavRail = useCallback(() => {
    const next = !isRail;
    setSessionOverride(next);
    // Only a neutral-context choice is a standing preference; overriding a
    // forced rail (narrow viewport, builder-canvas collapse) is a one-off.
    if (!isNarrowViewport && !forcedBuilderRail) {
      setStoredPreference(next);
      writeStoredPreference(next);
    }
  }, [isRail, isNarrowViewport, forcedBuilderRail]);

  // Cmd/Ctrl + \ mirrors the drawer chevron.
  useEffect(() => {
    if (!hasPermanentDrawer) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === '\\') {
        event.preventDefault();
        toggleNavRail();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasPermanentDrawer, toggleNavRail]);

  return {
    isRail,
    navWidth: isRail ? NAV_RAIL_WIDTH : DRAWER_WIDTH,
    toggleNavRail,
  };
};
